import { Box, Card, Grid2, Stack, styled, useMediaQuery } from '@mui/material';
import {
  GiftSubmissionBodyDtoFrequency,
  usePutValidateGiftSubmission
} from '@portal-middleware/hooks';
import { useGetSessionInfo } from '@sentinel/hooks';
import { formatCurrency } from '@vestwell-frontend/helpers';
import { useToggle } from '@vestwell-frontend/hooks';
import {
  Form,
  FormDropdown,
  FormField,
  FormRadioGroup,
  FormSaveButton,
  hexToRgba,
  Img,
  RadioButton,
  SwitchToggle,
  Text
} from '@vestwell-frontend/ui';

import { FormikProps } from 'formik';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { FrequnceyTextValues, GiftStepState } from '../config/CreateGiftState';
import { useGiftStateContext } from '../contexts/GiftStateContext';
import {
  FormikPropsWithServerError,
  FormValidationHelper
} from '../helpers/formValidationHelper';
import {
  canGiftAccountRecieveGifts,
  getBlockerMessageProps
} from '../helpers/giftAccountHelper';
import DayOfMonthFormDropdown from './DayOfMonthFormDropdown';
import { FormServerError } from './FormServerError';
import { GiftingAlertMessage } from './GiftingAlertMessage';
import { HeadingWithTwoSizes } from './HeadingWithTwoSizes';
import MonthFormDropdown from './MonthFormDropdown';
import { NeedHelpPhoneNumber } from './NeedHelpPhoneNumber';

const ImgAutoWidth = styled(Img)(({ theme }) => ({
  alignSelf: 'center',
  height: theme.spacing(7),
  width: 'auto',
  [theme.breakpoints.down('sm')]: {
    height: theme.spacing(12)
  }
}));

const amountPresetOptions = [25, 50, 100, 150, 250, 500];
const customValue = 'custom';

export type StartAGiftFormValues = FormikPropsWithServerError & {
  amount: number | string;
  customAmount: number;
  isRecurring: boolean;
  frequency: GiftSubmissionBodyDtoFrequency;
  month: number;
  dayOfMonth1: number;
  dayOfMonth2: number;
};

const StyledRecurringControlsStack = styled(Stack)(({ theme }) => ({
  '& > *': {
    alignSelf: 'center'
  },
  gap: theme.spacing(2)
}));

const StyledContainerStackParent = styled(Stack)(() => ({
  containerType: 'inline-size'
}));

const StyledContainerQueryStack = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  [theme.containerQueries.down(700)]: {
    flexDirection: 'column'
  }
}));

type StyledContainerQueryStackLevel2Props = {
  shouldWrap?: boolean;
};

const StyledTextDisplayNone = styled(
  Text
)<StyledContainerQueryStackLevel2Props>(({ theme, shouldWrap }) => ({
  [theme.containerQueries.down('sm')]: {
    display: shouldWrap ? 'none' : 'inline'
  }
}));

const StyledContainerQueryStackLevel2 = styled(
  Stack
)<StyledContainerQueryStackLevel2Props>(({ theme, shouldWrap }) => ({
  flexDirection: 'row',
  [theme.containerQueries.down('sm')]: {
    flexDirection: shouldWrap ? 'column' : 'row'
  }
}));

export const StyledHiddenIconRadioButton = styled(RadioButton)(({ theme }) => ({
  '& .MuiButtonBase-root': {
    opacity: 0,
    position: 'absolute',
    width: '1px'
  },
  backgroundColor: hexToRgba(theme.palette.grey50.light, 0.05),
  display: 'block',
  marginLeft: 0,
  minWidth: theme.spacing(0.5),
  textAlign: 'center'
}));

const CustomAmountStyledHiddenIconRadioButton = styled(
  StyledHiddenIconRadioButton
)(({ theme }) => ({
  marginBottom: theme.spacing(0)
}));

const dropdownStyles = {
  width: 'auto'
};
const StyledFormDropdown = styled(FormDropdown)(() => dropdownStyles);
const StyledMonthFormDropdown = styled(MonthFormDropdown)(() => dropdownStyles);
const StyledDayOfMonthFormDropdown = styled(DayOfMonthFormDropdown)(
  () => dropdownStyles
);

const StyledHeaderText = styled(Text)(({ theme }) => ({
  width: 'auto',
  [theme.breakpoints.down('sm')]: {
    textAlign: 'center',
    width: '100%'
  }
}));

export const StartAGift: FC = () => {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const { planId, giftingId } = useParams();
  const [canRecieveGifts, setCanRecieveGifts] = useToggle(true);
  const $form = useRef<FormikProps<StartAGiftFormValues>>(null);
  const { clearSessionIfNotLoggedIn, ctx, plan, giftAndAccount } =
    useGiftStateContext();

  clearSessionIfNotLoggedIn();
  const [isRecurring, toggleIsRecurring] = useToggle(
    ctx?.state?.giftSubmission?.isRecurring || false
  );

  useEffect(() => {
    setCanRecieveGifts(canGiftAccountRecieveGifts(giftAndAccount?.data?.data));
  }, [giftAndAccount]);

  const user = useGetSessionInfo('user', {
    query: {
      suspense: true
    }
  });

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        amount: yup.string().required('Amount is required'),
        customAmount: yup.string().when('amount', {
          is: value => isNaN(value),
          otherwise: yup.string().notRequired(),
          then: yup
            .string()
            .required('Custom amount is required')
            .test(
              'min',
              `Amount must be at least ${formatCurrency(plan.data?.data?.gift?.transactionLimits?.minimumDeposit)}`,
              value =>
                +value >=
                plan.data?.data?.gift?.transactionLimits?.minimumDeposit
            )
            .test(
              'max',
              `Amount must be less than ${formatCurrency(plan.data?.data?.gift?.transactionLimits?.maximumDeposit)}`,
              value =>
                +value <=
                  plan.data?.data?.gift?.transactionLimits?.maximumDeposit ||
                !plan.data?.data?.gift?.transactionLimits?.maximumDeposit
            )
        }),
        dayOfMonth1: yup.string().when('isRecurring', {
          is: () => isRecurring,
          otherwise: yup.string().notRequired(),
          then: yup.string().required('Day is required')
        }),
        dayOfMonth2: yup.string().when('isRecurring', {
          is: () =>
            isRecurring &&
            $form?.current?.values?.frequency ===
              GiftSubmissionBodyDtoFrequency.biMonthly,
          otherwise: yup.string().notRequired(),
          then: yup
            .string()
            .required('Second day is required')
            .test('differentDays', 'Select a different day', function (value) {
              return value !== this.parent.dayOfMonth1;
            })
        }),
        frequency: yup.string().when('isRecurring', {
          is: () => isRecurring,
          otherwise: yup.string().notRequired(),
          then: yup.string().required('Frequency is required')
        }),
        month: yup.string().when('isRecurring', {
          is: () =>
            isRecurring &&
            $form?.current?.values?.frequency ===
              GiftSubmissionBodyDtoFrequency.yearly,
          otherwise: yup.string().notRequired(),
          then: yup.string().required('Month is required')
        })
      }),
    [plan?.data?.data, isRecurring]
  );

  const frequencies = useMemo(
    () =>
      Object.values(GiftSubmissionBodyDtoFrequency).map(frequency => ({
        label: FrequnceyTextValues[frequency],
        value: frequency
      })),
    []
  );

  const blockerMessageProps = useMemo(() => {
    return getBlockerMessageProps(giftAndAccount?.data?.data, plan.data?.data);
  }, [canRecieveGifts]);

  const headerTextVariant = useMemo(() => (isMobile ? 'e1' : 'c1'), [isMobile]);

  const initialValues = useMemo(() => {
    return {
      amount: ctx?.state?.isCustomAmount
        ? customValue
        : (($form?.current?.values?.amount ||
            ctx?.state?.giftSubmission?.amount) ??
          ''),
      customAmount:
        (ctx?.state?.isCustomAmount && !$form?.current?.values?.amount
          ? ctx?.state?.giftSubmission?.amount
          : $form?.current?.values?.customAmount) ?? '',
      dayOfMonth1: ctx?.state?.giftSubmission?.recurrenceDays?.[0] ?? '',
      dayOfMonth2: ctx?.state?.giftSubmission?.recurrenceDays?.[1] ?? '',
      frequency: ctx?.state?.giftSubmission?.frequency ?? '',
      isRecurring: ctx?.state?.giftSubmission?.isRecurring ?? false,
      month: ctx?.state?.giftSubmission?.month ?? ''
    };
  }, [ctx?.state?.isCustomAmount, $form?.current?.values?.customAmount]);

  const putValidateGiftSubmission = usePutValidateGiftSubmission({
    mutation: {
      onError: error => {
        const errorMapper = ctx.state.isCustomAmount
          ? {
              amount: 'customAmount'
            }
          : null;
        FormValidationHelper.populateErrorsFromResponse(
          $form.current,
          error,
          errorMapper
        );
      },
      onSuccess: () => {
        ctx.setState({
          ...ctx?.state,
          createProfile:
            isRecurring &&
            !ctx?.state?.createProfile &&
            !ctx?.state?.isAuthenticated
              ? true
              : ctx?.state?.createProfile,
          giftAccountId: giftingId,
          giftSubmission: {
            ...ctx?.state?.giftSubmission,
            amount: ctx?.state?.isCustomAmount
              ? +$form.current.values.customAmount
              : +$form.current.values.amount,
            isRecurring,
            ...(isRecurring
              ? {
                  frequency: $form.current.values.frequency,
                  month: +$form.current.values.month,
                  recurrenceDays: [
                    +$form.current.values.dayOfMonth1 || 0,
                    $form.current.values.frequency ===
                    GiftSubmissionBodyDtoFrequency.biMonthly
                      ? +$form.current.values.dayOfMonth2 || 0
                      : 0
                  ].filter(v => v > 0)
                }
              : {})
          },
          isAuthenticated: !user?.data?.userId
            ? false
            : ctx?.state?.isAuthenticated,
          planId: planId,
          stepState: GiftStepState.occasion
        });
      }
    }
  });

  const onSubmit = useCallback(
    async data => {
      await putValidateGiftSubmission.mutateAsync({
        data: {
          amount: ctx?.state?.isCustomAmount
            ? +data.customAmount
            : +data.amount,
          isRecurring,
          payment: {
            ...ctx.state?.giftSubmission?.payment
          }
        },
        giftingId
      });
    },
    [$form?.current?.values, ctx.state?.giftSubmission, giftingId]
  );

  const onChange = useCallback(
    values => {
      if (values.amount) {
        ctx.setState({
          ...ctx.state,
          giftSubmission: {
            ...ctx.state?.giftSubmission,
            amount:
              values.amount === customValue
                ? values.customAmount
                : values.amount
          },
          isCustomAmount: values.amount === customValue
        });
      }
    },
    [$form?.current?.values?.amount]
  );

  if (!plan?.data?.data || !giftAndAccount?.data?.data) {
    return <></>;
  }

  return (
    <Stack mb={8}>
      <Stack
        alignItems={{ sm: 'flex-start', xs: 'center' }}
        direction={{ sm: 'row', xs: 'column-reverse' }}
        gap={2}
        justifyContent='space-between'
        paddingBottom={4}>
        {giftAndAccount.data?.data?.accountPublic && (
          <HeadingWithTwoSizes
            headingLevel={1}
            largeHeading={
              <StyledHeaderText
                fontWeight={600}
                mb={0}
                variant={headerTextVariant}>
                {giftAndAccount.data?.data.accountPublic?.firstName}
              </StyledHeaderText>
            }
            smallHeading={
              <StyledHeaderText mb={0} variant='k3'>
                Give a gift to
              </StyledHeaderText>
            }></HeadingWithTwoSizes>
        )}
        <Stack alignSelf='center' flexDirection='row'>
          <ImgAutoWidth
            alt={plan.data?.data?.shortName}
            src={`/assets/images/plan/${
              isMobile ? 'icon' : 'logo'
            }/${plan.data?.data?.id}.svg`}
          />
        </Stack>
      </Stack>
      <Card>
        {!canRecieveGifts && <GiftingAlertMessage {...blockerMessageProps} />}
        <Form
          initialValues={initialValues}
          onChange={onChange}
          onSubmit={onSubmit}
          ref={$form}
          validateOnChange
          validateOnMount
          validationSchema={validationSchema}>
          <StyledContainerStackParent direction='column' pt={3}>
            <FormRadioGroup
              align='flex-start'
              hideError={ctx?.state?.isCustomAmount}
              name='amount'
              position='left'
              variant='card'>
              <Grid2
                columnSpacing={{ md: 3, sm: 2, xs: 2 }}
                container
                rowSpacing={1}>
                {amountPresetOptions.map(amountPreset => {
                  return (
                    <Grid2
                      key={`Radio-button-amount-${amountPreset}`}
                      size={{ sm: 2, xs: 4 }}>
                      <StyledHiddenIconRadioButton
                        disabled={!canRecieveGifts}
                        label={
                          <Text mb={0} mt={1} variant='g2'>
                            {formatCurrency(amountPreset)}
                          </Text>
                        }
                        value={amountPreset}
                      />
                    </Grid2>
                  );
                })}
                <Grid2 size={12}>
                  <CustomAmountStyledHiddenIconRadioButton
                    disabled={!canRecieveGifts}
                    label={
                      <Text mb={0} mt={1} variant='g2'>
                        Custom amount
                      </Text>
                    }
                    value='custom'
                  />
                </Grid2>
              </Grid2>
            </FormRadioGroup>

            {ctx?.state?.isCustomAmount && (
              <Grid2 container>
                <Grid2 size={{ sm: 6, xs: 12 }}>
                  <FormField
                    format='currencyUs'
                    label='Custom amount'
                    name='customAmount'
                  />
                </Grid2>
              </Grid2>
            )}
            <StyledContainerQueryStack
              alignItems='baseline'
              gap={4}
              mb={2}
              mt={ctx?.state?.isCustomAmount ? 4 : 0}>
              <Box position='relative' top={isRecurring ? 10 : 0}>
                <SwitchToggle
                  aria-label='Switch setting your gift to be recurring'
                  checked={isRecurring}
                  data-testid='recurringSwitch'
                  disabled={!canRecieveGifts}
                  labels={{ on: 'Recurring' }}
                  onChange={toggleIsRecurring}
                />
              </Box>
              {isRecurring && (
                <StyledContainerQueryStackLevel2
                  gap={2}
                  mt={{ md: 0, sm: 4, xs: 4 }}
                  shouldWrap={
                    $form?.current?.values?.frequency !==
                    GiftSubmissionBodyDtoFrequency.monthly
                  }>
                  <StyledFormDropdown
                    hideLabel
                    label='Frequency'
                    name='frequency'
                    options={frequencies}
                    placeholder='Frequency'
                  />
                  <StyledRecurringControlsStack
                    direction={{ sm: 'row', xs: 'row' }}>
                    {$form?.current?.values?.frequency ===
                      GiftSubmissionBodyDtoFrequency.yearly && (
                      <StyledRecurringControlsStack direction='row'>
                        <StyledTextDisplayNone shouldWrap={true}>
                          in
                        </StyledTextDisplayNone>
                        <StyledMonthFormDropdown
                          hideLabel
                          label='month'
                          name='month'
                          placeholder='Month'
                        />
                      </StyledRecurringControlsStack>
                    )}
                    {$form?.current?.values?.frequency?.length > 0 && (
                      <>
                        <StyledTextDisplayNone
                          shouldWrap={
                            $form?.current?.values?.frequency ===
                            GiftSubmissionBodyDtoFrequency.biMonthly
                          }>
                          on the
                        </StyledTextDisplayNone>
                        <StyledDayOfMonthFormDropdown
                          currentMonth={$form?.current?.values?.month}
                          frequency={$form?.current?.values?.frequency}
                          hideLabel
                          label='Day of the month'
                          name='dayOfMonth1'
                          placeholder='Select'
                        />
                      </>
                    )}
                    {$form?.current?.values?.frequency ===
                      GiftSubmissionBodyDtoFrequency.biMonthly && (
                      <>
                        <Text>and the</Text>
                        <StyledDayOfMonthFormDropdown
                          hideLabel
                          label='Second Day of the month'
                          name='dayOfMonth2'
                          placeholder='Select'
                        />
                      </>
                    )}
                  </StyledRecurringControlsStack>
                </StyledContainerQueryStackLevel2>
              )}
            </StyledContainerQueryStack>
          </StyledContainerStackParent>

          <FormServerError formikRef={$form} />

          <FormSaveButton fullWidth>Continue</FormSaveButton>
        </Form>
        {canRecieveGifts && <NeedHelpPhoneNumber plan={plan.data?.data} />}
      </Card>
    </Stack>
  );
};
